import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '../components/Brand/Icon';
import { languageNames, readPreferredLanguage, rememberLanguage, persistAccountLanguage, validLanguage } from '../utils/languagePreference';
import { getLocalStorage, getUser, setLocalStorage } from '../utils/func';
import config from '../utils/config';
import changeLanguageInOrder from '../utils/changeLanguageInOrder';
import { abortable } from '../utils/abortable';
import { onboardingCopy } from './translations';
import { showConsentRequired } from '../legal/requiredNotice';
import './onboarding.scss';

export default function LanguageDialog({ onContinue }) {
  const { i18n } = useTranslation('brand', { useSuspense: false });
  const [selected, setSelected] = useState(() => {
    const language = String(readPreferredLanguage() || getUser()?.lng_code || i18n.resolvedLanguage || 'en').toLowerCase().split(/[-_]/)[0];
    return validLanguage(language) ? language : 'en';
  });
  const [saving, setSaving] = useState(false), [error, setError] = useState(false);
  const pending = useRef(false), controller = useRef(null), mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);
  const copy = onboardingCopy(selected);
  const save = async () => {
    if (pending.current) return;
    pending.current = true; setSaving(true); setError(false);
    const abort = new AbortController(); controller.current = abort;
    const token = getLocalStorage(config.token), user = getUser();
    const owns = () => mounted.current && token === getLocalStorage(config.token)
      && user?.id === getUser()?.id;
    const current = () => !abort.signal.aborted && owns();
    // Native transports and translation downloads may ignore cancellation.
    // Bound the whole choice so the introduction never traps a new user.
    const timer = setTimeout(() => abort.abort(), 25000);
    try {
      if (token && user?.id && selected !== user.lng_code) await abortable(persistAccountLanguage(token, user.id, selected, abort.signal), abort.signal);
      if (!current()) return;
      await abortable(changeLanguageInOrder(i18n, selected, current), abort.signal);
      if (!current()) return;
      rememberLanguage(selected);
      if (token && user?.id) setLocalStorage(config.user, JSON.stringify({ ...getUser(), lng_code: selected }));
      onContinue();
    } catch (failure) { if (owns() && !showConsentRequired(failure)) setError(true); }
    finally {
      clearTimeout(timer);
      if (controller.current === abort) {
        pending.current = false;
        if (mounted.current) setSaving(false);
      }
    }
  };
  return <Modal open centered title={copy.choose} footer={null} closable={false} maskClosable={false} keyboard={false}
    transitionName="" width={440} className="onboarding-language-modal">
    <div className="onboarding-language-options" role="radiogroup" aria-label={copy.choose} aria-busy={saving}>
      {Object.entries(languageNames).map(([language, name]) => <label key={language} className={selected === language ? 'selected' : ''}>
        <input type="radio" name="onboarding-language" value={language} checked={selected === language} disabled={saving}
          onChange={() => setSelected(language)} />
        <span lang={language}>{name}</span>
      </label>)}
    </div>
    {error && <p role="alert">{copy.error}</p>}
    <button type="button" className="onboarding-language-continue" onClick={save} disabled={saving}>
      {saving ? <Spin size="small" /> : <CheckIcon aria-hidden="true" />}<span>{copy.continue}</span>
    </button>
  </Modal>;
}
